// WHICH WAY EVERY MESH IN THE APP IS TURNED. One angle, shared, and remembered.
//
// A part is looked at in three places at once — the panel's viewer, the modal that opens
// out of it, and the strip of thumbnails underneath — and each of them used to frame its
// object from the same fixed default. Which is right for the FIRST look and wrong for
// every one after it: expanding a mesh threw away the angle you had just turned it to,
// and the thumbnails went on showing a side you had already decided was not the
// interesting one.
//
// The default was defended on comparability — every view uses the same angle, which is
// what makes two runs of one part comparable side by side. That argument survives intact
// here and is the reason this is ONE angle rather than one per viewer: the thumbnails
// still agree with each other and with the viewer above them. What changed is only that
// the shared angle can be moved.
//
// STORED AS A DIRECTION AND NOTHING ELSE — see MeshView. Not a camera position, which is
// in the units of one mesh framed for one viewport; and not a distance, which is the same
// problem one step removed. Meshes here run from an 8 mm dowel to a 733 mm stool, so how
// far out you pulled to read a thread is not a framing for a chair. Each view keeps its own
// distance and takes only the angle.
//
// Persisted for the same reason the unit toggle is: how you like to look at a part is a
// fact about you, not about the part, and re-aiming on every page load is a chore nobody
// asked for.

import type { MeshView } from './meshScene';

const KEY = 'notice.meshView';

const sane = (v: unknown): MeshView | null => {
  if (!v || typeof v !== 'object') return null;
  const { dir } = v as MeshView;
  if (!Array.isArray(dir) || dir.length !== 3) return null;
  if (!dir.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  // A zero direction is not an angle: it would put the camera on the object and leave
  // nothing to orbit around.
  if (dir.every((n) => n === 0)) return null;
  // Only the direction is kept, so a value stored when this also held a zoom still reads —
  // the extra key is simply dropped rather than making the whole angle unreadable.
  return { dir: [dir[0], dir[1], dir[2]] };
};

// THERE IS NO DEFAULT HERE, on purpose. Nothing stored means nobody has turned a mesh
// yet, and the right angle then is whatever framing the viewer computes for itself — which
// it already does, from the object's own bounding sphere. A constant written down here to
// mean "the usual angle" would be a second copy of that framing, and the two would agree
// until somebody adjusted one of them.
const read = (): MeshView | null => {
  try {
    return sane(JSON.parse(localStorage.getItem(KEY) || 'null'));
  } catch {
    // Anything unreadable is no preference, not a crash. This is a viewing angle, and the
    // worst case of losing it is looking at a part from the front.
    return null;
  }
};

let current: MeshView | null = read();
const listeners = new Set<(view: MeshView) => void>();

// Null until somebody has turned a mesh, which the viewer reads as "frame it yourself".
export const getMeshView = (): MeshView | null => current;

// Called by whichever viewer is being driven, on EVERY frame of a turn. Every view
// following along is re-aimed from here, which is what makes them turn WITH the mouse
// rather than snapping to wherever it stopped. Sixty vector assignments a second across a
// strip of thumbnails costs nothing; the jump did not look like nothing.
//
// `persist` is the half that must not run at that rate. Writing localStorage sixty times a
// second is a synchronous disk touch per frame, so the angle is only SAVED when the gesture
// settles — which is also the only moment worth saving, a drag being one decision and not
// three hundred.
export function setMeshView(view: MeshView, persist = true) {
  const next = sane(view);
  if (!next) return;
  current = next;
  if (persist) {
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* a private window, a full disk: the angle still works for this session */
    }
  }
  listeners.forEach((fn) => fn(next));
}

export function subscribeMeshView(fn: (view: MeshView) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
